import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { useIntl, FormattedMessage } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm, Controller } from 'react-hook-form';

// Components
import Heading from '../../../../Heading';
import Subheading from '../../../../Subheading';
import AppLink from '../../../../AppLink';
import ImageLoader from '../../../../ImageLoader';
import Button from '../../../../Button';
import PhoneField from '../../../../PhoneField';
import TextareaField from '../../../../TextareaField';
import InputField from '../../../../InputField';
import Success from '../Success';

// Router
import { getRoute } from '../../../../../entry/routes';

// State
import {
  fetchFeedbackData,
  resetFeedback,
  sendFeedback,
} from '../../../../../state/modules/feedback/actions';

// Utils
import { required, isEmail, requiredNumber, numberByCode } from '../../../../../utils/validators';

// Config
import config from '../../../../../config';

// Styles
import styles from './LiteForm.styl';

const cx = classNames.bind(styles);

export default function LiteForm(props) {
  const { className } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const { email = '', phone = '' } = useSelector(state => state.user.profile);
  const { isSent, isSending } = useSelector(state => state.feedback.sendingData);
  const id = useSelector(state => state.city.userCity.id);

  const [reCaptchaKey, setReCaptchaKey] = useState('');
  const [files, setFiles] = useState([]);

  const { register, handleSubmit, errors, control, setValue, reset } = useForm({
    defaultValues: { phone, email, text: '' },
  });

  const onSubmit = handleSubmit(formValues => {
    return dispatch(
      sendFeedback({
        ...formValues,
        file: files,
        g_recaptcha_response: reCaptchaKey,
        lite_form: true,
      }),
    );
  });

  const saveFiles = newFiles => {
    setFiles([...files, ...newFiles]);
  };

  const removeFiles = imageIndex => {
    const newFiles = files.filter((image, key) => key !== imageIndex);

    setFiles(newFiles);
  };

  const showForm = () => {
    dispatch(resetFeedback());
    reset();
    setFiles([]);
    setReCaptchaKey('');
  };

  useEffect(() => {
    dispatch(fetchFeedbackData(id));

    return () => {
      dispatch(resetFeedback());
    };
  }, []);

  useEffect(() => {
    if (phone) {
      setValue('phone', phone, { shouldValidate: true });
    }
  }, [phone]);

  useEffect(() => {
    if (email) {
      setValue('email', email, { shouldValidate: true });
    }
  }, [email]);

  if (isSent) {
    return <Success onClick={showForm} />;
  }

  return (
    <form className={cx('FeedbackForm', className)} onSubmit={onSubmit}>
      <Heading className={cx('FeedbackForm__title')} level={2} tagName="h2">
        {intl.formatMessage({ id: 'feedback.title' })}
      </Heading>
      <div className={cx('FeedbackForm__field')}>
        <Controller
          name="phone"
          control={control}
          rules={{ validate: { requiredNumber, numberByCode } }}
          render={({ onChange, value, name }) => (
            <PhoneField
              name={name}
              onChange={onChange}
              value={value}
              label={intl.formatMessage({ id: 'field.phone.label' })}
            />
          )}
        />
        {errors.phone && <div className={cx('FeedbackForm__error')}>{errors.phone.message}</div>}
      </div>
      <div className={cx('FeedbackForm__field')}>
        <InputField
          name="email"
          label={intl.formatMessage({ id: 'field.email.label' })}
          register={register({ validate: { required, isEmail } })}
        />
        {errors.email && <div className={cx('FeedbackForm__error')}>{errors.email.message}</div>}
      </div>
      <div className={cx('FeedbackForm__field')}>
        <TextareaField
          name="text"
          label={intl.formatMessage({ id: 'field.message.label' })}
          placeholder={intl.formatMessage({ id: 'field.feedbackMessage.placeholder' })}
          register={register({ validate: { required } })}
        />
        {errors.text && <div className={cx('FeedbackForm__error')}>{errors.text.message}</div>}
      </div>
      <ImageLoader
        className={cx('FeedbackForm__image-loader')}
        allowedExtensions="image/jpeg, image/gif, image/png"
        maxFileSize={32 * 1024 * 1024}
        maxFilesAmount={5}
        onLoadFiles={saveFiles}
        onRemoveFile={removeFiles}
      />
      <div className={cx('FeedbackForm__field')}>
        <ReCAPTCHA
          sitekey={config.reCaptchaSiteKey}
          onChange={value => setReCaptchaKey(value)}
        />
      </div>
      <Subheading className={cx('FeedbackForm__privacy')}>
        <FormattedMessage
          id="feedback.agree"
          values={{
            link: (
              <AppLink to={getRoute('personal-data-processing-policy')}>
                <FormattedMessage id="feedback.agree.link" />
              </AppLink>
            ),
          }}
        />
      </Subheading>
      <Button type="submit" isLoading={isSending} disabled={!reCaptchaKey}>
        {intl.formatMessage({ id: 'button.submit' })}
      </Button>
    </form>
  );
}

LiteForm.defaultProps = {
  className: '',
};

LiteForm.propTypes = {
  className: PropTypes.string,
};
